import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { IconButton } from '@/components/ui/button';
import { useDevRuntimeStore } from '@modules/dev/devRuntime';
import { useDevMode } from '@modules/dev/useDevMode';
import { SegmentationDevPanel } from '@segmentation/dev/SegmentationDevPanel';

type DevTabId = 'controls' | 'logs';

export function DevDock() {
  const enabled = useDevMode();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [tab, setTab] = useState<DevTabId>('controls');
  const logs = useDevRuntimeStore((state) => state.logs);
  const clearLogs = useDevRuntimeStore((state) => state.clearLogs);

  const phase = useMemo(() => {
    if (location.pathname.includes('/segmentation')) return 'segmentation';
    if (location.pathname.includes('/correction')) return 'correction';
    if (location.pathname.includes('/export')) return 'export';
    return 'scene';
  }, [location.pathname]);

  if (!enabled) return null;
  if (location.pathname.includes('/export')) return null;

  const content = (
    <aside className="pointer-events-auto fixed bottom-6 right-6 z-50 w-[420px] max-w-[calc(100vw-3rem)] overflow-hidden rounded-3xl border border-cyan-300/30 bg-slate-950/90 text-white shadow-2xl shadow-cyan-500/20 backdrop-blur-xl">
      <header className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.35em] text-cyan-200/70">Dev Dock</div>
          <div className="text-sm font-semibold capitalize text-white">{phase}</div>
        </div>
        <div className="flex items-center gap-2">
          <IconButton variant="ghost-light" size="sm" onClick={() => setCollapsed((value) => !value)}>
            {collapsed ? 'Open' : 'Collapse'}
          </IconButton>
          <IconButton
            variant="ghost-danger"
            size="sm"
            onClick={() => {
              // Turn the dock off by dropping ?dev=1 from the URL.
              const url = new URL(window.location.href);
              url.searchParams.delete('dev');
              window.location.href = url.toString();
            }}
          >
            Off
          </IconButton>
        </div>
      </header>

      {!collapsed && (
        <>
          <nav className="flex border-b border-white/10 px-3 py-2 text-xs">
            {(['controls', 'logs'] as DevTabId[]).map((item) => (
              <button
                key={item}
                onClick={() => setTab(item)}
                className={`rounded-xl px-3 py-2 font-semibold uppercase tracking-[0.25em] ${
                  tab === item ? 'bg-cyan-400/20 text-cyan-100' : 'text-white/50 hover:bg-white/10'
                }`}
              >
                {item}
              </button>
            ))}
          </nav>

          <div className="max-h-[70vh] overflow-auto p-4">
            {tab === 'controls' && (
              <>
                {phase === 'segmentation' ? (
                  <SegmentationDevPanel />
                ) : (
                  <p className="rounded-2xl border border-white/10 bg-black/30 p-3 text-sm text-white/70">
                    No dev plugin registered for this phase yet.
                  </p>
                )}
              </>
            )}

            {tab === 'logs' && (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-[0.25em] text-white/50">{logs.length} log entries</span>
                  <IconButton variant="ghost-light" size="sm" onClick={clearLogs}>
                    Clear
                  </IconButton>
                </div>
                <div className="max-h-[52vh] space-y-2 overflow-auto rounded-2xl border border-white/10 bg-black/40 p-3 font-mono text-[11px] leading-relaxed">
                  {logs.length === 0 ? (
                    <p className="text-white/40">No debug logs yet.</p>
                  ) : (
                    logs.map((entry) => (
                      <details key={entry.id} open={entry.kind === 'warn' || entry.kind === 'stop'}>
                        <summary className="cursor-pointer text-white/80">
                          <span className="text-cyan-200">{entry.time}</span>{' '}
                          <span className="text-amber-200">[{entry.kind}]</span>{' '}
                          <span>{entry.tag}</span> {entry.message}
                        </summary>
                        {entry.data !== undefined && (
                          <pre className="mt-2 overflow-auto rounded-xl bg-slate-900/80 p-2 text-white/70">
                            {JSON.stringify(entry.data, null, 2)}
                          </pre>
                        )}
                      </details>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </aside>
  );

  return createPortal(content, document.body);
}
